import React from 'react';
import pick from 'lodash/pick';

import noop from 'utils/noop';
import { EditorEvent, GraphCommonEvent } from '../../../../constants';
import { Graph, CommandEvent } from '../../../../types';
import { CommandManager } from '../../../../commands';
import {
  EditorContext,
  EditorPrivateContext,
  EditorContextProps,
  EditorPrivateContextProps,
} from '../../../../hooks';
import { shouldTriggerShortcut, isShortcutTriggered } from './Editor.utils';

const editorCallbacks = {
  onBeforeExecuteCommand: EditorEvent.onBeforeExecuteCommand,
  onAfterExecuteCommand: EditorEvent.onAfterExecuteCommand,
};

interface EditorProps {
  style?: React.CSSProperties;
  children: React.ReactNode;
  className?: string;
  /** function called right before any command is executed */
  [EditorEvent.onBeforeExecuteCommand]?: (e: CommandEvent) => void;
  /** function called right after any command is executed */
  [EditorEvent.onAfterExecuteCommand]?: (e: CommandEvent) => void;
}

interface EditorState extends EditorContextProps, EditorPrivateContextProps {}

export class Editor extends React.Component<EditorProps, EditorState> {
  static defaultProps = {
    [EditorEvent.onBeforeExecuteCommand]: noop,
    [EditorEvent.onAfterExecuteCommand]: noop,
  };

  cleanupEvents: (() => void)[] = [];
  lastMousedownTarget: HTMLElement | null = null;

  constructor(props: EditorProps) {
    super(props);

    this.state = {
      graph: null,
      setGraph: this.setGraph,
      executeCommand: this.executeCommand,
      commandManager: new CommandManager(),
    };

    this.lastMousedownTarget = null;
  }

  componentWillUnmount() {
    this.cleanupEvents.forEach((fn) => typeof fn === 'function' && fn());
    this.lastMousedownTarget = null;
    if (this.state.graph) {
      this.state.graph?.clear();
      this.state.graph?.destroy();
    }
  }

  bindEvent(graph: Graph) {
    const { props } = this;

    Object.values(editorCallbacks).forEach((e: EditorEvent) => {
      const fn = props[e as keyof typeof editorCallbacks];
      if (typeof fn === 'function') {
        // @ts-ignore
        graph.on(e, fn);
        this.cleanupEvents.push(() => graph.off(e, fn));
      }
    });
  }

  bindShortcut(graph: Graph) {
    window.addEventListener(GraphCommonEvent.onMouseDown, this.updateLastMousedownTarget);
    graph.on(GraphCommonEvent.onKeyDown, this.onGraphKeydown);
    this.cleanupEvents.push(() =>
      window.removeEventListener(GraphCommonEvent.onMouseDown, this.updateLastMousedownTarget),
    );
    this.cleanupEvents.push(() => graph?.off(GraphCommonEvent.onKeyDown, this.onGraphKeydown));
  }

  updateLastMousedownTarget = (e: MouseEvent) => {
    this.lastMousedownTarget = e.target as HTMLElement;
  };

  onGraphKeydown = (e: any) => {
    const { graph, commandManager } = this.state;

    if (!graph || !commandManager) return;
    if (!shouldTriggerShortcut(graph, this.lastMousedownTarget)) return;

    Object.values(commandManager.command).some((command) => {
      if (!isShortcutTriggered(command, e)) return false;
      if (!commandManager.canExecute(graph, command.name)) return false;

      e.preventDefault();
      this.executeCommand(command.name);

      return true;
    });
  };

  setGraph = (graph: Graph) => {
    this.setState({ graph });
    this.bindEvent(graph);
    this.bindShortcut(graph);

    graph.set('executeCommand', this.executeCommand);
  };

  executeCommand = (name: string, params?: Record<string, unknown>) => {
    const { graph, commandManager } = this.state;

    if (graph) {
      commandManager.execute(graph, name, params);
    }
  };

  render() {
    const { children } = this.props;
    const { graph, setGraph, executeCommand, commandManager } = this.state;

    return (
      <EditorContext.Provider value={{ graph, executeCommand, commandManager }}>
        <EditorPrivateContext.Provider value={{ setGraph, commandManager }}>
          <div {...pick(this.props, ['className', 'style', 'data-test'])}>{children}</div>
        </EditorPrivateContext.Provider>
      </EditorContext.Provider>
    );
  }
}

export default Editor;
